import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { BsPlusCircle } from "react-icons/bs";
import GiftHand from "../assets/icons/gifthand.svg";
import GiftSong from "../components/mockup/giftSong";
import MusicControl from "../components/musicControl";
import AudioPassivePlayer from "../components/common/audioPassivePlayer";
import { isURL } from "../utils/isURL";
import { isMobile } from "../utils/isMobile";
import { getSongsData } from "../services/contentApi";

const Listen = (props) => {
  const deviceMobile = isMobile();
  const showAvatarPreview = false;
  const navigate = useNavigate();

  const goToRedeem = () => {
    navigate("/redeem");
    props.pageChange("redeem");
    props.sceneChange("light");
  };

  const goToCreate = () => {
    navigate("/create");
    props.pageChange("create/playlist");
    props.sceneChange("light");
  };

  useEffect(() => {
    props.sceneChange("light");
    props.pageChange("listen");
  }, []);

  // Usa receivedGifts dalle props invece dei mock data
  const [gifts, setGifts] = useState([]);
  const [selectedGift, setSelectedGift] = useState(null);
  const [activeSection, setActiveSection] = useState("dedication");

  // Aggiorna gifts quando receivedGifts cambia
  useEffect(() => {
    if (props.receivedGifts && props.receivedGifts.length > 0) {
      setGifts((prevGifts) =>
        props.receivedGifts.map((newGift) => {
          const existing = prevGifts.find((g) => g.id === newGift.id);
          // se avevamo già le canzoni caricate per questo regalo, le manteniamo
          return existing && existing.songs.length > 0
            ? { ...newGift, songs: existing.songs }
            : newGift;
        })
      );

      setSelectedGift((prevSelected) => {
        // se c'è già un regalo selezionato e fa ancora parte della lista, mantienilo
        if (prevSelected) {
          const stillExists = props.receivedGifts.find(
            (g) => g.id === prevSelected.id
          );
          if (stillExists) {
            return prevSelected.songs.length > 0
              ? prevSelected
              : stillExists;
          }
        }
        // solo se non c'era nessuna selezione, seleziona il primo
        return props.receivedGifts[0];
      });
    }
  }, [props.receivedGifts]);

  // Carica le canzoni quando viene selezionato un regalo
  useEffect(() => {
    if (
      selectedGift &&
      selectedGift.idCollection &&
      selectedGift.songs.length === 0
    ) {
      getSongsData(selectedGift.idCollection)
        .then((res) => {
          if (res?.data?.contacts) {
            // Aggiorna il regalo con le canzoni
            setGifts((prevGifts) =>
              prevGifts.map((gift) =>
                gift.id === selectedGift.id
                  ? { ...gift, songs: res.data.contacts }
                  : gift
              )
            );
            // Aggiorna anche il selectedGift
            setSelectedGift((prev) => ({
              ...prev,
              songs: res.data.contacts,
            }));
          }
        })
        .catch((error) => {
          console.error("Error loading songs:", error);
        });
    }
  }, [selectedGift?.id, selectedGift?.idCollection]);

  // AUDIO MANAGEMENT
  const [currentSong, setCurrentSong] = useState("");
  const [volume, setVolume] = useState(75);
  const [songUrl, setSongUrl] = useState("");
  const [songPlaying, setSongPlaying] = useState("");
  const [songStart, setSongStart] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(30);
  const [pausedTime, setPausedTime] = useState(null);
  const [pausedSong, setPausedSong] = useState(null);
  const [seekToTime, setSeekToTime] = useState(null);

  function handlePlay(item) {
    if (props.userId) {
      // Se è lo stesso brano e era in pausa, riprendi da dove eri
      if (currentSong.ID_Song === item.ID_Song && pausedTime !== null) {
        setSongUrl(
          "https://song.tunehey.com/songs/" + item.Storage_Preview + ".mp3"
        );
        setSongStart(pausedTime); 
        setSeekToTime(pausedTime);
        setPausedTime(null);
        setPausedSong(null);
      } else {
        // Nuovo brano, inizia dall'inizio (o dal Preview_Start)
        setSongUrl(
          "https://song.tunehey.com/songs/" + item.Storage_Preview + ".mp3"
        );
        setCurrentSong(item);
        setSongStart(0);
        setSeekToTime(0);
        setPausedSong(null);
      }
      setSongPlaying(item.Storage_Preview);
    } else {
      props.setAcquisitionMsg("Accedi per poter ascoltare e salvare");
      props.showAcquisitionMsg();
    }
  }

  function handlePause() {
    setPausedTime(currentTime); // Salva il tempo corrente
    setPausedSong(songPlaying);
    setSongPlaying("");
    setSongUrl("");
  }

  const handlePauseRef = useRef(handlePause);
  useEffect(() => {
    handlePauseRef.current = handlePause;
  }); // nessun array di dipendenze: gira ad ogni render

  const displayTime = songUrl === "" && pausedTime !== null ? pausedTime : currentTime;
  const displaySong = songPlaying === "" && pausedSong !== null ? pausedSong : songPlaying;

  const handleTimeUpdate = (time, dur) => {
    if (songUrl === "") return; // blocca sia currentTime che duration spuri
    setCurrentTime(time);
    setDuration(dur);
  };

  const handleSeek = (time) => {
    if (songUrl === "") {
      // in pausa: aggiorna solo il tempo memorizzato
      setPausedTime(time);
      setCurrentTime(time);
    } else {
      // in riproduzione: seek reale sull'elemento audio
      setSeekToTime(time);
    }
  };

  function handleTogglePlayPause() {
    if (!currentSong) return;
    if (songUrl !== "") {
      handlePause();
    } else {
      handlePlay(currentSong);
    }
  }

  const handleTogglePlayPauseRef = useRef();
  useEffect(() => {
    handleTogglePlayPauseRef.current = handleTogglePlayPause;
  }); // NESSun array di dipendenze: gira ad ogni render, sempre aggiornato

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code !== "Space") return;

      const target = e.target;
      const isTyping =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable;

      if (isTyping) return;

      e.preventDefault();
      handleTogglePlayPauseRef.current(); // sempre l'ultima versione, mai stantia
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []); // registrato una volta sola, il ref fa il resto

  const renderAvatar = (avatar, size = "60px") => {
    const commonStyles = {
      width: size,
      height: size,
      borderRadius: "50%",
      objectFit: "cover",
    };

    if (!avatar || avatar === "") {
      return (
        <div
          style={{
            ...commonStyles,
            backgroundColor: "#E2D6BE",
          }}
        />
      );
    }

    // Se inizia con http o https, è già un URL completo dal CDN
    if (avatar.startsWith("http://") || avatar.startsWith("https://")) {
      return <img src={avatar} alt="Avatar" style={commonStyles} />;
    }

    // Se inizia con blob, è un'immagine locale temporanea
    if (avatar.startsWith("blob:")) {
      return <img src={avatar} alt="Avatar" style={commonStyles} />;
    }

    // Altrimenti è un filename locale nelle assets
    try {
      return (
        <img
          src={require(`../assets/images/mockup/${avatar}`)}
          alt="Avatar"
          style={commonStyles}
        />
      );
    } catch (error) {
      // Se il file non esiste, mostra il placeholder
      console.warn(`Avatar file not found: ${avatar}`);
      return (
        <div
          style={{
            ...commonStyles,
            backgroundColor: "#E2D6BE",
          }}
        />
      );
    }
  };

  const renderCover = (cover, size = "200px") => {
    const commonStyles = {
      width: size,
      height: size,
      borderRadius: "12px",
      objectFit: "cover",
    };

    if (!cover || cover === "") {
      return (
        <div
          style={{
            ...commonStyles,
            backgroundColor: "#E2D6BE",
          }}
        />
      );
    }

    // Se inizia con http o https, è già un URL completo dal CDN
    if (cover.startsWith("http://") || cover.startsWith("https://")) {
      return <img src={cover} alt="Cover" style={commonStyles} />;
    }

    // Se inizia con blob, è un'immagine locale temporanea
    if (cover.startsWith("blob:")) {
      return <img src={cover} alt="Cover" style={commonStyles} />;
    }

    // Altrimenti prova a caricare dalle assets locali
    try {
      // Controlla prima se è nella cartella mockup
      if (cover.startsWith("mockup")) {
        return (
          <img
            src={require(`../assets/images/mockup/${cover}`)}
            alt="Cover"
            style={commonStyles}
          />
        );
      }
      // Altrimenti prova nella cartella cover
      return (
        <img
          src={require(`../assets/cover/${cover}`)}
          alt="Cover"
          style={commonStyles}
        />
      );
    } catch (error) {
      // Se il file non esiste, mostra il placeholder
      console.warn(`Cover file not found: ${cover}`);
      return (
        <div
          style={{
            ...commonStyles,
            backgroundColor: "#E2D6BE",
          }}
        />
      );
    }
  };

  function handleSongEnd() {
  if (!selectedGift?.songs?.length || !currentSong) return;

  const currentIndex = selectedGift.songs.findIndex(
    (s) => s.ID_Song === currentSong.ID_Song
  );

  const nextSong = selectedGift.songs[currentIndex + 1];

  if (nextSong) {
    handlePlay(nextSong);
  } else {
    // era l'ultimo brano: reset stato player
    setCurrentSong("");
    setSongPlaying("");
    setSongUrl("");
    setPausedTime(null);
    setPausedSong(null);
  }
}

  function getAdjacentSong(offset) {
    if (!selectedGift?.songs?.length || !currentSong) return null;
    const currentIndex = selectedGift.songs.findIndex(
      (s) => s.ID_Song === currentSong.ID_Song
    );
    if (currentIndex === -1) return null;
    return selectedGift.songs[currentIndex + offset] || null;
  }

  const handleNextTrack = () => {
    const nextSong = getAdjacentSong(1);
    if (nextSong) handlePlay(nextSong);
  };

  const handlePreviousTrack = () => {
    const prevSong = getAdjacentSong(-1);
    if (prevSong) handlePlay(prevSong);
  };

  // MEDIA SESSION: controlli di sistema (lock screen / notifica) su Android e iOS
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;

    if (!currentSong) {
      navigator.mediaSession.metadata = null;
      return;
    }

    const cover = selectedGift?.cover;
    const artwork =
      cover && (cover.startsWith("http://") || cover.startsWith("https://"))
        ? [
            { src: cover, sizes: "96x96", type: "image/png" },
            { src: cover, sizes: "256x256", type: "image/png" },
            { src: cover, sizes: "512x512", type: "image/png" },
          ]
        : [];

    navigator.mediaSession.metadata = new window.MediaMetadata({
      title: currentSong.Title || "",
      artist: currentSong.Stage_Name || "",
      album: selectedGift?.giverName ? `${selectedGift.giverName}'s tune` : "",
      artwork,
    });
  }, [currentSong, selectedGift?.cover, selectedGift?.giverName]);

  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    navigator.mediaSession.playbackState = songUrl !== "" ? "playing" : "paused";
  }, [songUrl]);

  useEffect(() => {
    if (!("mediaSession" in navigator)) return;

    navigator.mediaSession.setActionHandler("play", () => {
      if (currentSong) handlePlay(currentSong);
    });
    navigator.mediaSession.setActionHandler("pause", handlePause);
    navigator.mediaSession.setActionHandler(
      "nexttrack",
      getAdjacentSong(1) ? handleNextTrack : null
    );
    navigator.mediaSession.setActionHandler(
      "previoustrack",
      getAdjacentSong(-1) ? handlePreviousTrack : null
    );

    return () => {
      navigator.mediaSession.setActionHandler("play", null);
      navigator.mediaSession.setActionHandler("pause", null);
      navigator.mediaSession.setActionHandler("nexttrack", null);
      navigator.mediaSession.setActionHandler("previoustrack", null);
    };
  }, [currentSong, selectedGift?.songs]);

  return (
    <div
      className="cream-background-color"
      style={{
        minHeight: "100vh",
        width: "100%",
        paddingTop: "10px",
        paddingBottom: "120px",
      }}
    >
      <div
        style={{
          maxWidth: "1600px",
          margin: "0 auto",
          padding: deviceMobile ? "20px" : "40px",
        }}
      >
        {/* Layout Desktop: Three columns (symmetric) */}
        {!deviceMobile ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "280px 1fr 280px",
              gap: "40px",
              alignItems: "start",
            }}
          >
            {/* LEFT COLUMN - Gift List */}
            <div
              style={{
                position: "sticky",
                top: "100px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  marginBottom: "16px",
                }}
              >
                <motion.button
                  onClick={goToRedeem}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    flex: 1,
                    cursor: "pointer",
                    padding: "12px",
                    borderRadius: "12px",
                    backgroundColor: "#E2D6BE",
                    border: "none",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <BsPlusCircle size={26} color="#2C2B26" />
                  <p
                    style={{
                      margin: 0,
                      fontWeight: 600,
                      fontSize: "13px",
                      color: "#2C2B26",
                      textAlign: "center",
                    }}
                  >
                    Riscatta un codice
                  </p>
                </motion.button>

                <motion.button
                  onClick={goToCreate}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    flex: 1,
                    cursor: "pointer",
                    padding: "12px",
                    borderRadius: "12px",
                    backgroundColor: "#E2D6BE",
                    border: "none",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <img src={GiftHand} alt="" style={{ width: "auto", height: "26px" }} />
                  <p
                    style={{
                      margin: 0,
                      fontWeight: 600,
                      fontSize: "13px",
                      color: "#2C2B26",
                      textAlign: "center",
                    }}
                  >
                    Regala un Tune
                  </p>
                </motion.button>
              </div>

              <div
                style={{
                  border: "2px solid #2C2B26",
                  borderRadius: "16px",
                  padding: "16px",
                }}
              >
                <h2
                  className="font-subtitle-OswMed"
                  style={{
                    marginTop: "0",
                    marginBottom: "16px",
                    fontSize: "18px",
                    textAlign: "center",
                  }}
                >
                  Regali ricevuti
                </h2>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  {gifts.map((gift) => (
                    <motion.div
                      key={gift.id}
                      onClick={() => {
                        setSelectedGift(gift);
                        setActiveSection("dedication");
                      }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      style={{
                        cursor: "pointer",
                        padding: "12px",
                        borderRadius: "12px",
                        backgroundColor: "#FFFDEF",
                        transition: "all 0.3s ease",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "10px",
                        border:
                          selectedGift.id === gift.id
                            ? "2px solid #FF6B35"
                            : "2px solid transparent",
                      }}
                    >
                      {renderCover(gift.cover, "100%")}
                      <div style={{ width: "100%", textAlign: "center" }}>
                        <p
                          style={{
                            margin: 0,
                            fontWeight: 600,
                            fontSize: "14px",
                            color: "#FF6B35",
                          }}
                        >
                          {gift.giverName}'s tune
                        </p>
                        <p
                          style={{
                            margin: "4px 0 0 0",
                            fontSize: "12px",
                            opacity: 0.7,
                            color: "#2C2B26",
                          }}
                        >
                          {gift.songs?.length || 0} brani
                        </p>
                      </div>
                      {showAvatarPreview && (
                        <div style={{ marginTop: "4px" }}>
                          {renderAvatar(gift.avatar, "40px")}
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            {/* CENTER COLUMN - Gift Detail */}
            <div>
              {selectedGift && (
                <motion.div
                  key={selectedGift.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  style={{
                    maxWidth: "700px",
                    margin: "0 auto",
                  }}
                >
                  {/* Header con nome e avatar */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "16px",
                      marginBottom: "32px",
                    }}
                  >
                    {renderAvatar(selectedGift.avatar, "80px")}
                    <h1
                      className="font-title-Osw"
                      style={{
                        margin: 0,
                        fontSize: "36px",
                      }}
                    >
                      {selectedGift.giverName}'s tune
                    </h1>
                  </div>

                  {/* Cover */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      marginBottom: "32px",
                    }}
                  >
                    {renderCover(selectedGift.cover, "300px")}
                  </div>

                  {/* Switcher per Dedication/Moment */}
                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      marginBottom: "16px",
                      justifyContent: "center",
                    }}
                  >
                    <button
                      onClick={() => setActiveSection("dedication")}
                      style={{
                        padding: "10px 24px",
                        borderRadius: "20px",
                        border: "none",
                        backgroundColor:
                          activeSection === "dedication"
                            ? "#FF6B35"
                            : "#E2D6BE",
                        color:
                          activeSection === "dedication"
                            ? "#FFFDEF"
                            : "#2C2B26",
                        fontWeight: 600,
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        fontSize: "14px",
                      }}
                    >
                      Dedica
                    </button>
                    <button
                      onClick={() => setActiveSection("moment")}
                      style={{
                        padding: "10px 24px",
                        borderRadius: "20px",
                        border: "none",
                        backgroundColor:
                          activeSection === "moment" ? "#FF6B35" : "#E2D6BE",
                        color:
                          activeSection === "moment" ? "#FFFDEF" : "#2C2B26",
                        fontWeight: 600,
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        fontSize: "14px",
                      }}
                    >
                      Momento speciale
                    </button>
                  </div>

                  {/* Content area with animation */}
                  <div style={{ marginBottom: "40px", minHeight: "150px" }}>
                    <AnimatePresence mode="wait">
                      {activeSection === "dedication" &&
                        selectedGift.dedication && (
                          <motion.div
                            key="dedication"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            transition={{ duration: 0.3 }}
                            style={{
                              padding: "24px",
                              backgroundColor: "#FFFDEF",
                              borderRadius: "16px",
                            }}
                          >
                            <p
                              style={{
                                margin: 0,
                                whiteSpace: "pre-line",
                                fontSize: "16px",
                                lineHeight: "1.7",
                                color: "#2C2B26",
                              }}
                            >
                              {selectedGift.dedication}
                            </p>
                          </motion.div>
                        )}

                      {activeSection === "moment" && selectedGift.moment && (
                        <motion.div
                          key="moment"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.3 }}
                          className="yellow-background-color"
                          style={{
                            padding: "24px",
                            borderRadius: "16px",
                          }}
                        >
                          <p
                            style={{
                              margin: 0,
                              fontWeight: 600,
                              marginBottom: "12px",
                              fontSize: "16px",
                              color: "#2C2B26",
                            }}
                          >
                            Per un ascolto speciale:
                          </p>
                          <p
                            style={{
                              margin: 0,
                              whiteSpace: "pre-line",
                              fontSize: "16px",
                              lineHeight: "1.7",
                              color: "#2C2B26",
                            }}
                          >
                            {selectedGift.moment}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Songs List */}
                  <div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      {selectedGift.songs?.map((song, index) => (
                        <GiftSong
                          key={index}
                          data={song}
                          isInteractive={false}
                          isWebPlayer={true}
                          removeSong={() => {}}
                          playingSong={displaySong}
                          onPlay={handlePlay}
                          onPause={handlePause}
                        />
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* RIGHT COLUMN - Empty (symmetric) */}
            <div style={{ position: "sticky", top: "100px" }}>
              {/* Vuoto ma mantiene la simmetria */}
            </div>
          </div>
        ) : (
          /* Layout Mobile: Vertical Stack */
          <div>
            {/* Access buttons */}
            <div
              style={{
                display: "flex",
                gap: "10px",
                marginBottom: "16px",
              }}
            >
              <motion.button
                onClick={goToRedeem}
                whileTap={{ scale: 0.98 }}
                style={{
                  flex: 1,
                  cursor: "pointer",
                  padding: "10px",
                  borderRadius: "12px",
                  backgroundColor: "#E2D6BE",
                  border: "none",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <BsPlusCircle size={22} color="#2C2B26" />
                <p
                  style={{
                    margin: 0,
                    fontWeight: 600,
                    fontSize: "13px",
                    color: "#2C2B26",
                    textAlign: "center",
                  }}
                >
                  Riscatta un codice
                </p>
              </motion.button>

              <motion.button
                onClick={goToCreate}
                whileTap={{ scale: 0.98 }}
                style={{
                  flex: 1,
                  cursor: "pointer",
                  padding: "10px",
                  borderRadius: "12px",
                  backgroundColor: "#E2D6BE",
                  border: "none",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <img src={GiftHand} alt="" style={{ width: "auto", height: "22px" }} />
                <p
                  style={{
                    margin: 0,
                    fontWeight: 600,
                    fontSize: "13px",
                    color: "#2C2B26",
                    textAlign: "center",
                  }}
                >
                  Regala un Tune
                </p>
              </motion.button>
            </div>

            {/* Gift List */}
            <div
              style={{
                border: "2px solid #2C2B26",
                borderRadius: "16px",
                padding: "12px",
                marginBottom: "32px",
              }}
            >
              <h2
                className="font-subtitle-OswMed"
                style={{
                  marginTop: "0",
                  marginBottom: "16px",
                  fontSize: "18px",
                  textAlign: "center",
                }}
              >
                Regali ricevuti
              </h2>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                {gifts.map((gift) => (
                  <motion.div
                    key={gift.id}
                    onClick={() => {
                      setSelectedGift(gift);
                      setActiveSection("dedication");
                    }}
                    whileTap={{ scale: 0.98 }}
                    style={{
                      cursor: "pointer",
                      padding: "10px",
                      borderRadius: "12px",
                      backgroundColor:  "#FFFDEF",
                      transition: "all 0.3s ease",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      border:
                        selectedGift.id === gift.id
                          ? "2px solid #FF6B35"
                          : "2px solid transparent",
                    }}
                  >
                    {renderCover(gift.cover, "50px")}
                    <div style={{ flex: 1 }}>
                      <p
                        style={{
                          margin: 0,
                          fontWeight: 600,
                          fontSize: "14px",
                          color: "#FF6B35",
                        }}
                      >
                        {gift.giverName}'s tune
                      </p>
                      <p
                        style={{
                          margin: "2px 0 0 0",
                          fontSize: "12px",
                          opacity: 0.7,
                          color: "#2C2B26",
                        }}
                      >
                        {gift.songs?.length || 0} brani
                      </p>
                    </div>
                    {showAvatarPreview && renderAvatar(gift.avatar, "35px")}
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Gift Detail */}
            {selectedGift && (
              <motion.div
                key={selectedGift.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                {/* Header */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "24px",
                    justifyContent: "center",
                  }}
                >
                  {renderAvatar(selectedGift.avatar, "60px")}
                  <h1
                    className="font-title-Osw"
                    style={{
                      margin: 0,
                      fontSize: "24px",
                    }}
                  >
                    {selectedGift.giverName}'s tune
                  </h1>
                </div>

                {/* Cover */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginBottom: "24px",
                  }}
                >
                  {renderCover(selectedGift.cover, "220px")}
                </div>

                {/* Switcher */}
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    marginBottom: "16px",
                    justifyContent: "center",
                  }}
                >
                  <button
                    onClick={() => setActiveSection("dedication")}
                    style={{
                      padding: "8px 20px",
                      borderRadius: "20px",
                      border: "none",
                      backgroundColor:
                        activeSection === "dedication" ? "#FF6B35" : "#E2D6BE",
                      color:
                        activeSection === "dedication" ? "#FFFDEF" : "#2C2B26",
                      fontWeight: 600,
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      fontSize: "13px",
                    }}
                  >
                    Dedica
                  </button>
                  <button
                    onClick={() => setActiveSection("moment")}
                    style={{
                      padding: "8px 20px",
                      borderRadius: "20px",
                      border: "none",
                      backgroundColor:
                        activeSection === "moment" ? "#FF6B35" : "#E2D6BE",
                      color: activeSection === "moment" ? "#FFFDEF" : "#2C2B26",
                      fontWeight: 600,
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      fontSize: "13px",
                    }}
                  >
                    Momento speciale
                  </button>
                </div>

                {/* Content */}
                <div style={{ marginBottom: "32px", minHeight: "120px" }}>
                  <AnimatePresence mode="wait">
                    {activeSection === "dedication" &&
                      selectedGift.dedication && (
                        <motion.div
                          key="dedication"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.3 }}
                          style={{
                            padding: "20px",
                            backgroundColor: "#FFFDEF",
                            borderRadius: "12px",
                          }}
                        >
                          <p
                            style={{
                              margin: 0,
                              whiteSpace: "pre-line",
                              fontSize: "14px",
                              lineHeight: "1.7",
                              color: "#2C2B26",
                            }}
                          >
                            {selectedGift.dedication}
                          </p>
                        </motion.div>
                      )}

                    {activeSection === "moment" && selectedGift.moment && (
                      <motion.div
                        key="moment"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.3 }}
                        className="yellow-background-color"
                        style={{
                          padding: "20px",
                          borderRadius: "12px",
                        }}
                      >
                        <p
                          style={{
                            margin: 0,
                            fontWeight: 600,
                            marginBottom: "10px",
                            fontSize: "14px",
                            color: "#2C2B26",
                          }}
                        >
                          Per un ascolto speciale:
                        </p>
                        <p
                          style={{
                            margin: 0,
                            whiteSpace: "pre-line",
                            fontSize: "14px",
                            lineHeight: "1.7",
                            color: "#2C2B26",
                          }}
                        >
                          {selectedGift.moment}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Songs */}
                <div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    {selectedGift.songs?.map((song, index) => (
                      <GiftSong
                        key={index}
                        data={song}
                        isInteractive={false}
                        isWebPlayer={true}
                        removeSong={() => {}}
                        playingSong={songPlaying}
                        onPlay={handlePlay}
                        onPause={handlePause}
                      />
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        )}
      </div>

      {/* Music Player - Fixed at bottom */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 7000,
        }}
      >
        <MusicControl
          currentSong={currentSong}
          isPlaying={songUrl !== ""}
          isChosed={false}
          isWebPlayer={true}
          onPlay={handlePlay}
          onPause={handlePause}
          volume={volume}
          setVolume={setVolume}
          onSelect={() => {}} // in questa pagina non occorre selezionare
          onRemove={() => {}}
          currentTime={displayTime}
          duration={duration}
          onSeek={handleSeek}
        />
      </div>

      <AudioPassivePlayer
        songUrl={songUrl}
        songStart={songStart}
        handlePause={handlePause}
        onEnded={handleSongEnd}
        type={"previewPlayer"}
        volume={volume}
        onTimeUpdate={handleTimeUpdate}
        currentTime={currentTime}
        seekTo={seekToTime}
        isWebPlayer={true} // Importante: disabilita il limite di 30 secondi
      />
    </div>
  );
};

export default Listen;
